import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { enquiryMessage, openWhatsApp } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", cls: "", school: "", subject: "", contact: "" };

const inputCls =
  "w-full rounded-xl border border-forest/15 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35 transition-colors duration-300 focus:border-terra focus:outline-none focus:ring-2 focus:ring-terra/25";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-[0.18em] text-forest/70";
const Err = ({ id, msg }) => (msg ? <p data-testid={id} className="mt-1 text-xs font-semibold text-red-600">{msg}</p> : null);

export const EnquiryForm = ({ compact = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter the student's name";
    if (!form.cls.trim()) errs.cls = "Please enter the class";
    if (!form.school.trim()) errs.school = "Please enter the school name";
    if (!form.subject.trim()) errs.subject = "Please enter the subject";
    if (!/^\d{10}$/.test(form.contact.trim())) errs.contact = "Enter a valid 10-digit number";
    if (!accepted) errs.terms = "Please accept the terms and conditions to continue";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
    try {
      await fetch(`${API}/enquiries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    } catch {
      /* WhatsApp redirect still proceeds */
    }
    toast.success("Redirecting you to WhatsApp to confirm your enquiry…");
    openWhatsApp(enquiryMessage(payload));
    setForm(EMPTY);
    setAccepted(false);
    setErrors({});
    setSending(false);
  };

  return (
    <form data-testid="enquiry-form" onSubmit={submit} noValidate className={compact ? "" : "rounded-3xl border border-terra/30 bg-white p-6 shadow-[0_30px_80px_-40px_rgba(28,35,51,0.35)] sm:p-8"}>
      <div className="space-y-4">
        <div>
          <label htmlFor="enquiry-name" className={labelCls}>Name</label>
          <input id="enquiry-name" data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder="Student's full name" className={inputCls} />
          <Err id="enquiry-name-error" msg={errors.name} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="enquiry-class" className={labelCls}>Class</label>
            <input id="enquiry-class" data-testid="enquiry-class-input" value={form.cls} onChange={set("cls")} placeholder="e.g. 10th / 12th / IPMAT" className={inputCls} />
            <Err id="enquiry-class-error" msg={errors.cls} />
          </div>
          <div>
            <label htmlFor="enquiry-school" className={labelCls}>School</label>
            <input id="enquiry-school" data-testid="enquiry-school-input" value={form.school} onChange={set("school")} placeholder="Student's school" className={inputCls} />
            <Err id="enquiry-school-error" msg={errors.school} />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="enquiry-subject" className={labelCls}>Subject</label>
            <input id="enquiry-subject" data-testid="enquiry-subject-input" value={form.subject} onChange={set("subject")} placeholder="e.g. Maths, Commerce" className={inputCls} />
            <Err id="enquiry-subject-error" msg={errors.subject} />
          </div>
          <div>
            <label htmlFor="enquiry-contact" className={labelCls}>Contact Number</label>
            <input id="enquiry-contact" data-testid="enquiry-contact-input" value={form.contact} onChange={set("contact")} placeholder="10-digit mobile number" inputMode="numeric" maxLength={10} className={inputCls} />
            <Err id="enquiry-contact-error" msg={errors.contact} />
          </div>
        </div>
      </div>
      <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-forest/10 bg-ivory p-4">
        <input
          type="checkbox"
          data-testid="enquiry-terms-checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#0E5A47]"
        />
        <span className="text-sm leading-relaxed text-charcoal/80">I have read and I accept all the terms and conditions mentioned.</span>
      </label>
      <Err id="enquiry-terms-error" msg={errors.terms} />
      <button
        data-testid="enquiry-submit-button"
        type="submit"
        disabled={sending}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-forest px-6 py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:scale-[1.02] hover:bg-[#0F141F] disabled:opacity-60"
      >
        <Send className="h-4 w-4" strokeWidth={2} />
        {sending ? "Opening WhatsApp…" : "Send Enquiry on WhatsApp"}
      </button>
      <p className="mt-3 text-center text-[11px] leading-relaxed text-charcoal/45">
        Your details open pre-filled in WhatsApp — just tap Send once. Nothing is shared publicly.
      </p>
    </form>
  );
};
